import {
  allConditions,
  ConditionGroup,
  hasDuplicateConditionGroups,
} from '../headerAnalysisRuleModel';

export const DUPLICATE_RULE_NAME_ERROR_CODE = 'DUPLICATE_RULE_NAME';
export const DUPLICATE_CONDITION_GROUP_ERROR_CODE = 'DUPLICATE_CONDITION_GROUP';
export const HEADER_ANALYSIS_RULE_FIELD_MAX_LENGTH = 255;

export interface HeaderAnalysisRuleFieldErrors {
  ruleName?: string;
  description?: string;
  score?: string;
  emptyConditions?: string;
  duplicateConditionGroups?: string;
  conditionDetails: Record<string, string>;
}

export interface HeaderAnalysisRuleValidationMessages {
  required: string;
  duplicateName: string;
  ruleNameTooLong: string;
  descriptionTooLong: string;
  invalidScore: string;
  duplicateCondition: string;
  duplicateConditionGroups: string;
  conditionDetailsRequired: string;
  triggerDetailsRequired: string;
  requiredFields: string;
}

interface RuleFields {
  ruleName: string;
  description: string;
  threatScoreContribution: number;
}

interface RuleFieldPatch {
  ruleName?: string;
  description?: string;
  threatScoreContribution?: number;
}

interface RuleValidationInput {
  fields: RuleFields;
  tree: ConditionGroup;
  resolvedRules: any[];
  source: 'Global' | 'Zone';
  savedRuleId: string;
  messages: HeaderAnalysisRuleValidationMessages;
}

export const emptyFieldErrors = (): HeaderAnalysisRuleFieldErrors => ({
  conditionDetails: {},
});

export const hasDuplicateRuleName = (
  name: string,
  resolvedRules: any[],
  source: 'Global' | 'Zone',
  savedRuleId: string,
): boolean => {
  const normalizedName = name.trim().toLowerCase();
  if (!normalizedName) return false;
  return resolvedRules.some(rule => (
    rule?.source === source
    && String(rule?.id) !== String(savedRuleId)
    && String(rule?.name || '').trim().toLowerCase() === normalizedName
  ));
};

export const clearFieldErrorsForPatch = (
  current: HeaderAnalysisRuleFieldErrors,
  patch: RuleFieldPatch,
): HeaderAnalysisRuleFieldErrors => {
  const next = {...current, conditionDetails: {...current.conditionDetails}};
  if (patch.ruleName !== undefined) delete next.ruleName;
  if (patch.description !== undefined) delete next.description;
  if (patch.threatScoreContribution !== undefined) delete next.score;
  return next;
};

export const reconcileConditionErrors = (
  current: HeaderAnalysisRuleFieldErrors,
  tree: ConditionGroup,
): HeaderAnalysisRuleFieldErrors => {
  const blankConditionIds = new Set(
    allConditions(tree).filter(condition => !condition.check).map(condition => condition.id),
  );
  const conditionDetails = Object.fromEntries(
    Object.entries(current.conditionDetails).filter(([id]) => blankConditionIds.has(id)),
  );
  return {
    ...current,
    emptyConditions: tree.children.length ? undefined : current.emptyConditions,
    duplicateConditionGroups: undefined,
    conditionDetails,
  };
};

export const validateHeaderAnalysisRule = ({
  fields,
  tree,
  resolvedRules,
  source,
  savedRuleId,
  messages,
}: RuleValidationInput): HeaderAnalysisRuleFieldErrors => {
  const errors = emptyFieldErrors();
  const trimmedRuleName = fields.ruleName.trim();

  if (!trimmedRuleName) errors.ruleName = messages.required;
  if (trimmedRuleName.length > HEADER_ANALYSIS_RULE_FIELD_MAX_LENGTH) {
    errors.ruleName = messages.ruleNameTooLong;
  }
  if (!errors.ruleName && hasDuplicateRuleName(
    fields.ruleName,
    resolvedRules,
    source,
    savedRuleId,
  )) {
    errors.ruleName = messages.duplicateName;
  }
  if (!fields.description.trim()) errors.description = messages.required;
  if (fields.description.length > HEADER_ANALYSIS_RULE_FIELD_MAX_LENGTH) {
    errors.description = messages.descriptionTooLong;
  }
  if (
    !Number.isInteger(Number(fields.threatScoreContribution))
    || Number(fields.threatScoreContribution) < 0
    || Number(fields.threatScoreContribution) > 100
  ) {
    errors.score = messages.invalidScore;
  }
  if (!tree.children.length) {
    errors.emptyConditions = messages.required;
  } else {
    allConditions(tree).forEach(condition => {
      if (!condition.check) {
        errors.conditionDetails[condition.id] = messages.conditionDetailsRequired;
      }
    });
    if (
      !Object.keys(errors.conditionDetails).length
      && hasDuplicateConditionGroups(tree)
    ) {
      errors.duplicateConditionGroups = messages.duplicateConditionGroups;
    }
  }
  return errors;
};

export const hasTriggerValidationError = (
  errors: HeaderAnalysisRuleFieldErrors,
): boolean => Boolean(
  errors.emptyConditions
  || errors.duplicateConditionGroups
  || Object.keys(errors.conditionDetails).length,
);

export const isRuleValid = (errors: HeaderAnalysisRuleFieldErrors): boolean => (
  !errors.ruleName
  && !errors.description
  && !errors.score
  && !hasTriggerValidationError(errors)
);

export const validationToastMessage = (
  errors: HeaderAnalysisRuleFieldErrors,
  fields: RuleFields,
  messages: HeaderAnalysisRuleValidationMessages,
): string => {
  if (errors.emptyConditions || Object.keys(errors.conditionDetails).length) {
    return messages.triggerDetailsRequired;
  }
  if (errors.duplicateConditionGroups) return errors.duplicateConditionGroups;
  if (!fields.ruleName.trim() || !fields.description.trim()) return messages.requiredFields;
  return '';
};

export const updateDuplicateRuleNameError = (
  current: HeaderAnalysisRuleFieldErrors,
  duplicate: boolean,
  duplicateMessage: string,
): HeaderAnalysisRuleFieldErrors => {
  if (duplicate) return {...current, ruleName: duplicateMessage};
  if (current.ruleName !== duplicateMessage) return current;
  const next = {...current};
  delete next.ruleName;
  return next;
};

export const isDuplicateRuleNameResponse = (error: any): boolean => (
  error?.response?.status === 422
  && error?.response?.data?.detail?.code === DUPLICATE_RULE_NAME_ERROR_CODE
);

export const isDuplicateConditionGroupResponse = (error: any): boolean => (
  error?.response?.status === 422
  && error?.response?.data?.detail?.code === DUPLICATE_CONDITION_GROUP_ERROR_CODE
);
